import React, { useEffect, useState } from 'react';
import Button from '../../../components/ui/Button';

const PREVIEW_PAGE_SIZE = 25;

// Conserva la vista previa mientras el usuario navega entre pestañas.
// No vuelve a consultar el backend si la selección no cambió.
const pricingPreviewCache = new Map();
// MB_CACHED_PRICE_APPLY_V5
const pricingPreviewPartial = new Map();
// MB_TABLA_APLICAR_V3
const pricingInputSnapshots = new Map();

const buildPricingPreviewCacheKey = (products = []) =>
  products.map((product, index) => [
    product?.id ??
      product?.asin ??
      product?.amazon_asin ??
      index,
    product?.category_id ??
      product?.categoria ??
      product?.category ??
      '',
    product?.amazonPriceUsd ??
      product?.amazon_price_usd ??
      '',
    product?.forceEnd999 ?? ''
  ].join(':')).join('|');

export const getVerifiedPricingRows = (products = []) => {
  if (!Array.isArray(products) || !products.length) return null;

  const key = buildPricingPreviewCacheKey(products);
  const rows = pricingPreviewCache.get(key);
  const snapshot = pricingInputSnapshots.get(key);

  if (!Array.isArray(rows) || !snapshot ||
      rows.length !== products.length ||
      snapshot.length !== products.length) {
    return null;
  }

  if (!products.every((product, i) => JSON.stringify(snapshot[i]) === JSON.stringify(product))) {
    return null;
  }

  return rows;
};

const formatNumber = (value, decimals = 2) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;

  return new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(number);
};

const formatCop = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;

  return `$${new Intl.NumberFormat('es-CO', {
    maximumFractionDigits: 0
  }).format(number)}`;
};

const resolveImage = (product) => {
  const urls = [];

  const add = (value) => {
    if (!value) return;

    if (Array.isArray(value)) {
      value.forEach(add);
      return;
    }

    if (typeof value === 'object') {
      add(
        value.source ??
        value.url ??
        value.secure_url ??
        value.src
      );
      return;
    }

    if (typeof value !== 'string') return;

    value
      .split(/[,;\n\r\t|]+/)
      .map(v => v.trim())
      .filter(v => /^https?:\/\//i.test(v))
      .forEach(v => urls.push(v));
  };

  add(product?.images);
  add(product?.highResolutionImages);
  add(product?.['Imágenes de alta resolución']);
  add(product?.pictures);
  add(product?.image);
  add(product?.imagen);

  return urls[0] || '';
};

export const getAvailablePricingRows = (products = []) => {
  if (!Array.isArray(products) || !products.length) return [];

  const key = buildPricingPreviewCacheKey(products);
  const rows =
    pricingPreviewCache.get(key) ||
    pricingPreviewPartial.get(key) ||
    [];

  return products.map((product, index) => {
    const row = rows[index];

    const id =
      product?.id ??
      product?.asin ??
      product?.amazon_asin ??
      index;

    const asin = String(
      product?.asin ||
      product?.ASIN ||
      product?.amazon_asin ||
      product?.originalAsin ||
      '-'
    ).trim().toUpperCase();

    return row &&
      String(row.id) === String(id) &&
      String(row.asin || '').trim().toUpperCase() === asin
        ? row
        : null;
  });
};

const PricingPreview = ({ selectedProducts = [] }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [refreshKey, setRefreshKey] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewImageClosing, setPreviewImageClosing] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('todos');
  const [tamanoPagina, setTamanoPagina] = useState(PREVIEW_PAGE_SIZE);
  const [pagina, setPagina] = useState(1);

  const previewProducts = Array.isArray(selectedProducts)
    ? selectedProducts
    : [];

  const previewCacheKey =
    buildPricingPreviewCacheKey(previewProducts) +
    '::' + JSON.stringify(previewProducts);

  const textoBusqueda = busqueda.trim().toLowerCase();

  const filasFiltradas = rows.filter(row => {
    if (
      textoBusqueda &&
      !`${row.title} ${row.asin}`.toLowerCase().includes(textoBusqueda)
    ) return false;

    if (filtro === 'errores') return !row.ok;

    if (filtro === 'sin-peso') {
      return !(Number(row.weightLb) > 0);
    }

    if (filtro === 'sin-dimensiones') {
      return !String(row.dimensions || '').trim();
    }

    return true;
  });

  const totalPaginas = Math.max(
    1, Math.ceil(filasFiltradas.length / tamanoPagina)
  );

  const paginaActual = Math.min(pagina, totalPaginas);

  const filasPagina = filasFiltradas.slice(
    (paginaActual - 1) * tamanoPagina,
    paginaActual * tamanoPagina
  );

  useEffect(() => {
    let cancelled = false;

    const products = previewProducts;

    const calculate = async () => {
      setRows([]);
      if (!products.length) {
        setRows([]);
        setLoading(false);
        return;
      }

      const cachedRows =
        pricingPreviewCache.get(previewCacheKey);

      if (cachedRows) {
        setRows(cachedRows);
        setProgress({
          done: cachedRows.length,
          total: products.length
        });
        setLoading(false);
        return;
      }

      setLoading(true);

      const previous =
        pricingPreviewPartial.get(previewCacheKey) || [];

      setRows([...previous]);
      setProgress({
        done: previous.length,
        total: products.length
      });

      const apiBase =
        import.meta.env?.VITE_API_BASE_URL ||
        window.location.origin;

      const results = [
        ...(pricingPreviewPartial.get(previewCacheKey) || [])
      ];

      let completed = results.length;

      for (let index = results.length; index < products.length; index++) {
        if (cancelled) return;

        const product = products[index];

        const row = {
          id: product?.id ?? product?.asin ?? index,
          image: resolveImage(product),
          title:
            product?.title ??
            product?.titulo ??
            product?.['título'] ??
            'Sin título',
          asin:
            product?.asin ??
            product?.amazon_asin ??
            product?.originalAsin ??
            '-',
          category:
            product?.category_id ??
            product?.categoria ??
            product?.category ??
            '-',
          amazonUsd: Number(
            product?.amazonPriceUsd ??
            product?.amazon_price_usd
          ),
          dimensions: String(
            product?.mercadoLibreDimensions ??
            product?.mercado_libre_dimensions ?? ''
          ).split(',')[0]
        };

        try {
          const response = await fetch(
            `${apiBase}/api/pricing/calculate`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ product })
            }
          );

          const data = await response.json().catch(() => ({}));

          if (
            !response.ok ||
            data?.success === false ||
            !Number.isFinite(Number(data?.finalPriceCop))
          ) {
            throw new Error(
              data?.message ||
              data?.error ||
              'No se pudo calcular'
            );
          }

          results.push({
            ...row,
            ok: true,
            weightLb: data.billableWeightLb,
            dimensions: String(
              data.mercadoLibreDimensions ?? row.dimensions ?? ''
            ).split(',')[0],
            transportUsd: data.transportUsd,
            landedCostCop: data.landedCostCop,
            feeCop: data.mercadoLibreFeeCop,
            shippingCop: data.mercadoLibreShippingCostCop,
            profitPercent: data.profitPercent,
            finalPriceCop: data.finalPriceCop
          });
        } catch (error) {
          results.push({
            ...row,
            ok: false,
            error: error?.message || 'Falta información'
          });
        }

        completed += 1;

        if (!cancelled) {
          pricingPreviewPartial.set(
            previewCacheKey,
            [...results]
          );
        }

        if (
          !cancelled &&
          (completed % 10 === 0 || completed === products.length)
        ) {
          setRows([...results]);
          setProgress({
            done: completed,
            total: products.length
          });
        }
      }

      if (!cancelled) {
        pricingPreviewCache.set(
          previewCacheKey,
          [...results]
        );
        pricingInputSnapshots.set(previewCacheKey, [...products]);
        setLoading(false);
      }
    };

    calculate();

    return () => {
      cancelled = true;
    };
  }, [previewCacheKey, refreshKey]);

  const closePreviewImage = () => {
    if (!previewImage || previewImageClosing) return;

    setPreviewImageClosing(true);

    window.setTimeout(() => {
      setPreviewImage(null);
      setPreviewImageClosing(false);
    }, 180);
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div>
          <div className="font-semibold text-text-primary">
            Vista previa de cálculo
          </div>

          <div className="text-xs text-text-secondary mt-1">
            Se calculan todos los productos seleccionados.
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          iconName="RefreshCw"
          disabled={loading}
          onClick={() => {
            pricingPreviewCache.delete(previewCacheKey);
            setRefreshKey(v => v + 1);
          }}
        >
          Actualizar vista previa
        </Button>
      </div>

      {loading && (
        <div className="px-4 py-2 text-xs text-text-secondary border-b border-border">
          Calculando {progress.done}/{progress.total}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border">
        <input
          type="search"
          value={busqueda}
          onChange={e => {
            setBusqueda(e.target.value);
            setPagina(1);
          }}
          placeholder="Buscar título o ASIN"
          aria-label="Buscar producto"
          className="min-w-[180px] flex-1 rounded-lg border border-border bg-surface px-3 py-2 text-sm"
        />

        <select
          value={filtro}
          onChange={e => {
            setFiltro(e.target.value);
            setPagina(1);
          }}
          aria-label="Filtrar resultados"
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm"
        >
          <option value="todos">Todos</option>
          <option value="errores">Con errores</option>
          <option value="sin-peso">Sin peso calculado</option>
          <option value="sin-dimensiones">Sin dimensiones disponibles</option>
        </select>

        <select
          value={tamanoPagina}
          onChange={e => {
            setTamanoPagina(Number(e.target.value));
            setPagina(1);
          }}
          aria-label="Productos por página"
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm"
        >
          <option value={25}>25 por página</option>
          <option value={50}>50 por página</option>
          <option value={100}>100 por página</option>
        </select>

        <span className="text-xs text-text-secondary">
          {filasFiltradas.length} de {rows.length} calculados
        </span>
      </div>

      <div className="overflow-auto max-h-[430px]">
        <table className="w-full min-w-[1450px] text-xs">
          <thead className="sticky top-0 bg-surface z-10">
            <tr className="border-b border-border">
              <th className="px-3 py-3 text-left">#</th>
              <th className="px-3 py-3 text-left">Imagen</th>
              <th className="px-3 py-3 text-left">Producto</th>
              <th className="px-3 py-3 text-left">ASIN</th>
              <th className="px-3 py-3 text-left">Categoría ML</th>
              <th className="px-3 py-3 text-right">Amazon USD</th>
              <th className="px-3 py-3 text-right">Peso lb</th>
              <th className="px-3 py-3 text-right">Dimensiones cm</th>
              <th className="px-3 py-3 text-right">Transporte USD</th>
              <th className="px-3 py-3 text-right">Costo aterrizado</th>
              <th className="px-3 py-3 text-right">Comisión ML</th>
              <th className="px-3 py-3 text-right">Envío ML</th>
              <th className="px-3 py-3 text-right">Utilidad</th>
              <th className="px-3 py-3 text-right">Precio final</th>
            </tr>
          </thead>

          <tbody>
            {filasPagina.map((row, index) => (
              <tr
                key={`${row.id}-${index}`}
                className="border-b border-border"
              >
                <td className="px-3 py-3">{(paginaActual - 1) * tamanoPagina + index + 1}</td>

                <td className="px-3 py-2">
                  {row.image ? (
                    <img
                      src={row.image}
                      alt={row.title}
                      className="w-12 h-12 object-contain rounded border border-border bg-white cursor-zoom-in transition-transform duration-200 hover:scale-110"
                      loading="lazy"
                      onClick={() => {
                        setPreviewImageClosing(false);
                        setPreviewImage({
                          src: row.image,
                          title: row.title
                        });
                      }}
                    />
                  ) : (
                    <div className="w-12 h-12 flex items-center justify-center border border-border rounded text-[10px] text-text-secondary">
                      Sin imagen
                    </div>
                  )}
                </td>

                <td className="px-3 py-3 min-w-[280px]">
                  <div className="font-medium text-text-primary">
                    {row.title}
                  </div>

                  {!row.ok && (
                    <div className="text-error mt-1">
                      {row.error}
                    </div>
                  )}
                </td>

                <td className="px-3 py-3">
                  {/^[A-Z0-9]{10}$/.test(String(row.asin ?? '').trim().toUpperCase()) ? (
                    <a
                      href={`https://www.amazon.com/dp/${String(row.asin).trim().toUpperCase()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline hover:text-blue-800"
                      title="Ver producto en Amazon"
                    >
                      {row.asin} ↗
                    </a>
                  ) : (
                    row.asin
                  )}
                </td>
                <td className="px-3 py-3">{row.category}</td>

                <td className="px-3 py-3 text-right">
                  {formatNumber(row.amazonUsd) ?? 'Falta dato'}
                </td>

                <td className="px-3 py-3 text-right">
                  {row.ok
                    ? formatNumber(row.weightLb)
                    : 'Pendiente'}
                </td>

                <td className="px-3 py-3 text-right">
                  {row.dimensions || 'Pendiente'}
                </td>

                <td className="px-3 py-3 text-right">
                  {row.ok
                    ? formatNumber(row.transportUsd)
                    : 'Pendiente'}
                </td>

                <td className="px-3 py-3 text-right">
                  {row.ok
                    ? formatCop(row.landedCostCop)
                    : 'Pendiente'}
                </td>

                <td className="px-3 py-3 text-right">
                  {row.ok
                    ? formatCop(row.feeCop)
                    : 'Pendiente'}
                </td>

                <td className="px-3 py-3 text-right">
                  {row.ok
                    ? formatCop(row.shippingCop)
                    : 'Pendiente'}
                </td>

                <td className="px-3 py-3 text-right">
                  {row.ok
                    ? `${formatNumber(row.profitPercent)}%`
                    : 'Pendiente'}
                </td>

                <td className="px-3 py-3 text-right font-semibold">
                  {row.ok
                    ? formatCop(row.finalPriceCop)
                    : 'Pendiente'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border">
        <span className="text-xs text-text-secondary">
          Página {paginaActual} de {totalPaginas}
          {' · '}{filasFiltradas.length} resultados
        </span>

        <div className="flex gap-2">
          <button
            type="button"
            disabled={paginaActual <= 1}
            onClick={() => setPagina(paginaActual - 1)}
            className="rounded-lg border border-border px-3 py-2 text-sm disabled:opacity-40"
          >
            Anterior
          </button>

          <button
            type="button"
            disabled={paginaActual >= totalPaginas}
            onClick={() => setPagina(paginaActual + 1)}
            className="rounded-lg border border-border px-3 py-2 text-sm disabled:opacity-40"
          >
            Siguiente
          </button>
        </div>
      </div>
      {previewImage && (
        <>
          <style>{`
            @keyframes pricingBackdropIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }

            @keyframes pricingBackdropOut {
              from { opacity: 1; }
              to { opacity: 0; }
            }

            @keyframes pricingImageIn {
              from {
                opacity: 0;
                transform: scale(0.92) translateY(8px);
              }
              to {
                opacity: 1;
                transform: scale(1) translateY(0);
              }
            }

            @keyframes pricingImageOut {
              from {
                opacity: 1;
                transform: scale(1) translateY(0);
              }
              to {
                opacity: 0;
                transform: scale(0.94) translateY(6px);
              }
            }

            .pricing-preview-backdrop-open {
              animation: pricingBackdropIn 220ms ease-out forwards;
            }

            .pricing-preview-backdrop-close {
              animation: pricingBackdropOut 180ms ease-in forwards;
            }

            .pricing-preview-image-open {
              animation: pricingImageIn 240ms cubic-bezier(.2,.8,.2,1) forwards;
            }

            .pricing-preview-image-close {
              animation: pricingImageOut 180ms ease-in forwards;
            }

            @media (prefers-reduced-motion: reduce) {
              .pricing-preview-backdrop-open,
              .pricing-preview-backdrop-close,
              .pricing-preview-image-open,
              .pricing-preview-image-close {
                animation: none !important;
              }
            }
          `}</style>

          <div
            className={`fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 ${
              previewImageClosing
                ? 'pricing-preview-backdrop-close'
                : 'pricing-preview-backdrop-open'
            }`}
            onClick={closePreviewImage}
          >
            <div
              className={`relative max-w-[90vw] max-h-[90vh] rounded-xl bg-white p-3 shadow-2xl ${
                previewImageClosing
                  ? 'pricing-preview-image-close'
                  : 'pricing-preview-image-open'
              }`}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                aria-label="Cerrar imagen"
                onClick={closePreviewImage}
                className="absolute -right-3 -top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-2xl font-semibold text-gray-700 shadow-lg transition-transform duration-150 hover:scale-110 hover:bg-gray-100"
              >
                ×
              </button>

              <img
                src={previewImage.src}
                alt={previewImage.title || 'Producto'}
                className="max-h-[80vh] max-w-[86vw] rounded-lg object-contain"
              />

              {previewImage.title && (
                <div className="mt-3 max-w-[86vw] px-2 text-center text-sm font-medium text-gray-700">
                  {previewImage.title}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PricingPreview;
